import {Form} from "react-router-dom";
import React, {useRef, useState, useMemo, useEffect, useCallback} from "react";
import {toast} from "react-toastify";
import {Autocomplete, useLoadScript} from "@react-google-maps/api";

import AddOrgMutation from "./mutations/AddOrg";
import Loading from "../common/Loading";
import DualListSelector from "../common/DualListSelector";
import {CURRENCIES} from "../../constants/vars";
import {GlobalStyles} from "../../constants/styles";
import FilePicker from "../common/FilePicker";
import {useMutation} from "@apollo/client";
import useActiveProfile from "../common/hooks/useActiveProfile";
import {UPDATE_ORGANIZATION_LOGO} from "./mutations/UpdateOrganizationLogo";
import useThemeColors from "./portal/useThemeColors";
import {alphaFromHex} from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";
import {getLocalizedGraphQLError} from "../../util/graphqlError";

export default function AddOrgModal({
                                        show,
                                        onClose,
                                        onSubmit,
                                        organizations,
                                        setOrganizations,
                                        fields,
                                        waivers,
                                        sanctionRules,
                                        stripeAccounts,
                                    }) {
    const [addOrgMutation] = AddOrgMutation();
    const {activeProfile} = useActiveProfile();
    const colors = useThemeColors();
    const {t} = useTranslation();

    const scopeId = useMemo(
        () => `kbola-add-org-${Math.random().toString(36).slice(2, 9)}`,
        []
    );

    const [logoFile, setLogoFile] = useState(null);
    const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);

    useEffect(() => {
        if (!logoFile) {
            setLogoPreviewUrl(null);
            return;
        }
        const url = URL.createObjectURL(logoFile);
        setLogoPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [logoFile]);

    const clearLogo = useCallback(() => {
        setLogoFile(null);
        setLogoPreviewUrl(null);
    }, []);

    const [updateOrganizationLogo] = useMutation(UPDATE_ORGANIZATION_LOGO);

    const [searchResult, setSearchResult] = useState("");
    const fieldAddressInput = useRef(null);

    const [location, setLocation] = useState({
        address: "",
        city: "",
        state: "",
        country: "",
        countryCode: "",
        latitude: null,
        longitude: null,
        placeId: "",
    });

    const [selectedFields, setSelectedFields] = useState([]);
    const [selectedWaiverIds, setSelectedWaiverIds] = useState([]);
    const selectedWaiverTitles = useMemo(() => {
        const selectedSet = new Set((selectedWaiverIds || []).map((id) => String(id)));
        return (waivers || [])
            .filter((waiver) => selectedSet.has(String(waiver.id)))
            .map((waiver) => waiver.title);
    }, [selectedWaiverIds, waivers]);

    const {isLoaded} = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
        libraries: ["places"],
    });

    const isSubmittingDisabled = !activeProfile;

    const modalStyles = useMemo(() => {
        const primary = colors.primary;

        return {
            wrap: {
                borderRadius: 16,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 18px 44px rgba(0,0,0,0.18)",
            },
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(primary, 0.50)},
                    ${alphaFromHex(primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            titleWrap: {display: "flex", alignItems: "center", gap: 10},
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.04em",
                padding: "6px 10px",
                borderRadius: 999,
                color: colors.textOnPrimary,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
            },
            title: {
                margin: 0,
                fontSize: 16,
                fontWeight: 900,
                color: "#14151a",
            },
            closeBtn: {
                width: 36,
                height: 36,
                borderRadius: 10,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
            },
            body: {
                padding: 14,
                background: "#fff",
            },
            sectionTitle: {
                margin: "2px 0 10px",
                fontSize: 13,
                fontWeight: 900,
                color: "rgba(20,21,26,0.78)",
                letterSpacing: "0.02em",
                textTransform: "uppercase",
            },
            card: {
                borderRadius: 14,
                padding: 12,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.015)",
                marginBottom: 12,
            },
            label: {
                fontSize: 12.5,
                fontWeight: 800,
                color: "rgba(20,21,26,0.70)",
                marginBottom: 6,
            },
            input: {
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.12)",
                padding: "10px 12px",
                fontWeight: 700,
                outline: "none",
            },
            select: {
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.12)",
                padding: "0px 12px",
                fontWeight: 800,
                outline: "none",
                backgroundColor: "#fff",
            },
            hint: {
                marginTop: 6,
                color: "rgba(20,21,26,0.55)",
                fontSize: 12.5,
                fontWeight: 600,
                lineHeight: 1.25,
            },
            footer: {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 14px",
                background: "rgba(0,0,0,0.02)",
                borderTop: "1px solid rgba(0,0,0,0.06)",
            },
            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
            },
            btnGhost: {
                background: "#fff",
                border: "1px solid rgba(0,0,0,0.12)",
                color: "#14151a",
            },
            btnPrimary: {
                background: GlobalStyles.colors.canary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
            },
        };
    }, [colors]);

    if (!isLoaded) return <Loading/>;
    if (!show) return null;

    function onLoad(autocomplete) {
        setSearchResult(autocomplete);
    }

    function onPlaceChanged() {
        if (searchResult != null) {
            const place = searchResult.getPlace();

            const locationObj = {
                address: place.formatted_address,
                city: "",
                state: "",
                country: "",
                countryCode: "",
                latitude: place.geometry?.location?.lat(),
                longitude: place.geometry?.location?.lng(),
                placeId: place.place_id,
            };

            place.address_components?.forEach((component) => {
                const types = component.types || [];
                if (types.includes("locality")) locationObj.city = component.long_name;
                if (types.includes("administrative_area_level_1")) locationObj.state = component.long_name;
                if (types.includes("country")) {
                    locationObj.country = component.long_name;
                    locationObj.countryCode = component.short_name || "";
                }
            });

            setLocation(locationObj);
        } else {
            alert(t("addOrganization.pleaseEnterText"));
        }
    }

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!activeProfile?.id) {
            toast.error(t("addOrganization.noActiveProfile"), {
                autoClose: 3000,
                toastId: "org_no_profile",
                position: "top-center",
                closeOnClick: true,
            });
            return;
        }

        const fieldIds = selectedFields.map((f) => f.id);

        try {
            const formElements = e.currentTarget.elements;
            const res = await addOrgMutation({
                variables: {
                    name: formElements.namedItem("name").value,
                    organizationType: formElements.namedItem("type").value,
                    profileId: activeProfile.id,
                    location,
                    fieldIds,
                    waiverIds: selectedWaiverIds,
                    stripeAccountId: formElements.namedItem("stripeAccountId").value || null,
                    currency: formElements.namedItem("currency").value || null,
                },
            });

            const org = res?.data?.createTeam?.organization;

            if (org) {
                let finalOrg = org;

                if (logoFile) {
                    try {
                        const up = await updateOrganizationLogo({
                            variables: {
                                teamId: org.id,
                                file: logoFile,
                            },
                        });

                        const payload = up?.data?.updateOrganizationLogo;

                        if (!payload?.success) {
                            toast.warn(
                                t("addOrganization.logoUploadFailedWithErrors", {
                                    errors: (payload?.errors || []).join(", "),
                                }),
                                {
                                    autoClose: 4500,
                                    toastId: "org_logo_failed",
                                    position: "top-center",
                                }
                            );
                        } else if (payload?.organization) {
                            finalOrg = payload.organization;
                        }
                    } catch (error) {
                        toast.warn(
                            t("addOrganization.logoUploadFailedWithMessage", {
                                message: error.message,
                            }),
                            {
                                autoClose: 4500,
                                toastId: "org_logo_failed",
                                position: "top-center",
                            }
                        );
                    }
                }

                toast.success(t("addOrganization.createdSuccess"), {
                    autoClose: 3000,
                    toastId: "org_added",
                    position: "top-center",
                    closeOnClick: true,
                });

                setOrganizations([...organizations, finalOrg]);
                try {
                    const auth = JSON.parse(localStorage.getItem("auth") || "null");
                    const profile = auth?.user?.profiles?.find((item) => String(item.id) === String(activeProfile?.id));
                    if (profile && finalOrg?.id) {
                        profile.profileOrganizations = [
                            ...(profile.profileOrganizations || []).filter(
                                (item) => String(item.organization?.id) !== String(finalOrg.id)
                            ),
                            {
                                status: "accepted",
                                organization: finalOrg,
                                group: {id: "1", name: "League Manager"},
                            },
                        ];
                        localStorage.setItem("auth", JSON.stringify(auth));
                        window.dispatchEvent(new Event("kbola-auth-updated"));
                    }
                } catch {
                }
                setSelectedWaiverIds([]);
                onClose();
                onSubmit?.();
            }
        } catch (err) {
            toast.error(getLocalizedGraphQLError(err, t, "addOrganization.createError"), {
                autoClose: 3000,
                toastId: "org_error",
                position: "top-center",
                closeOnClick: true,
            });
        }
    };

    return (
        <>
            <div
                className="modal fade show d-block"
                id="add-org-modal"
                tabIndex="-1"
                role="dialog"
                aria-modal="true"
            >
                <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
                    <div className="modal-content" style={modalStyles.wrap}>
                        <Form method="post" onSubmit={handleSubmit}>
                            <div className="modal-header" style={modalStyles.header}>
                                <div style={modalStyles.titleWrap}>
                                    <span style={modalStyles.badge}>
                                        {t("addOrganization.newBadge")}
                                    </span>
                                    <h4 className="modal-title" style={modalStyles.title}>
                                        {t("addOrganization.title")}
                                    </h4>
                                </div>

                                <button
                                    type="button"
                                    onClick={onClose}
                                    aria-label={t("addOrganization.close")}
                                    style={modalStyles.closeBtn}
                                    title={t("addOrganization.close")}
                                >
                                    <span aria-hidden="true" style={{fontSize: 20, lineHeight: 1}}>
                                        &times;
                                    </span>
                                </button>
                            </div>

                            <div className="modal-body" style={modalStyles.body}>
                                <div style={modalStyles.card}>
                                    <div style={modalStyles.sectionTitle}>
                                        {t("addOrganization.basics")}
                                    </div>

                                    <FilePicker
                                        scopeId={scopeId}
                                        colors={colors}
                                        label={t("addOrganization.logoLabel")}
                                        hint={t("addOrganization.logoHint")}
                                        accept="image/*"
                                        value={logoFile}
                                        previewUrl={logoPreviewUrl}
                                        onChangeFile={(file) => setLogoFile(file)}
                                        onClear={clearLogo}
                                    />

                                    <div className="form-group" style={{marginBottom: 12}}>
                                        <label style={modalStyles.label}>
                                            {t("addOrganization.name")}
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            required
                                            name="name"
                                            placeholder={t("addOrganization.namePlaceholder")}
                                            style={modalStyles.input}
                                        />
                                    </div>

                                    <div className="form-group" style={{marginBottom: 0}}>
                                        <label style={modalStyles.label}>
                                            {t("addOrganization.organizationType")}
                                        </label>
                                        <select
                                            className="form-control"
                                            name="type"
                                            required
                                            style={modalStyles.select}
                                        >
                                            <option value="league">
                                                {t("addOrganization.typeLeague")}
                                            </option>
                                            <option value="academy">
                                                {t("addOrganization.typeAcademy")}
                                            </option>
                                        </select>
                                        <div style={modalStyles.hint}>
                                            {t("addOrganization.organizationTypeHint")}
                                        </div>
                                    </div>
                                </div>

                                <div style={modalStyles.card}>
                                    <div style={modalStyles.sectionTitle}>
                                        {t("addOrganization.location")}
                                    </div>

                                    <div className="form-group" style={{marginBottom: 0}}>
                                        <label style={modalStyles.label}>
                                            {t("addOrganization.address")}
                                        </label>
                                        <Autocomplete onPlaceChanged={onPlaceChanged} onLoad={onLoad}>
                                            <input
                                                className="form-control"
                                                type="text"
                                                placeholder={t("addOrganization.addressPlaceholder")}
                                                ref={fieldAddressInput}
                                                style={modalStyles.input}
                                            />
                                        </Autocomplete>
                                        <div style={modalStyles.hint}>
                                            {t("addOrganization.addressHint")}
                                        </div>
                                    </div>
                                </div>

                                <div style={modalStyles.card}>
                                    <div
                                        style={{
                                            ...modalStyles.sectionTitle,
                                            color: GlobalStyles.colors.woodsmoke,
                                        }}
                                    >
                                        {t("addOrganization.venues")}
                                    </div>

                                    <DualListSelector
                                        title={t("addOrganization.venues")}
                                        description=""
                                        leftTitle={t("addOrganization.availableFields")}
                                        rightTitle={t("addOrganization.selectedFields")}
                                        items={fields}
                                        selected={selectedFields}
                                        getItemLabel={(f) => `${f.venue?.name || ""} ${f.name}`}
                                        onChange={(updatedSelectedFields) => setSelectedFields(updatedSelectedFields)}
                                    />
                                </div>

                                <div style={modalStyles.card}>
                                    <div style={modalStyles.sectionTitle}>
                                        {t("editOrganization.policiesAndRules")}
                                    </div>

                                    <div className="form-group" style={{marginBottom: 0}}>
                                        <label style={modalStyles.label}>
                                            {t("editOrganization.waiverOptional")}
                                        </label>
                                        <div className="kbola-waiver-picker-wrap">
                                            <select
                                                className="form-control kbola-waiver-picker"
                                                name="waiverIds"
                                                style={modalStyles.select}
                                                multiple
                                                value={selectedWaiverIds}
                                                onChange={(e) =>
                                                    setSelectedWaiverIds(
                                                        Array.from(e.target.selectedOptions).map((opt) => opt.value)
                                                    )
                                                }
                                                size={Math.min(6, Math.max(3, waivers?.length || 0))}
                                            >
                                                {waivers?.map((w) => (
                                                    <option key={w.id} value={w.id}>
                                                        {w.title}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        {selectedWaiverTitles.length > 0 && (
                                            <div className="kbola-waiver-chip-row">
                                                {selectedWaiverTitles.map((title, index) => (
                                                    <span className="kbola-waiver-chip" key={`${title}-${index}`}>
                                                        {title}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div style={modalStyles.card}>
                                    <div style={modalStyles.sectionTitle}>
                                        {t("addOrganization.billing")}
                                    </div>

                                    <div className="form-group" style={{marginBottom: 12}}>
                                        <label style={modalStyles.label}>
                                            {t("addOrganization.currency")}
                                        </label>
                                        <select
                                            className="form-control"
                                            name="currency"
                                            required
                                            style={modalStyles.select}
                                        >
                                            <option value="">
                                                {t("addOrganization.selectCurrency")}
                                            </option>
                                            {CURRENCIES?.map((c) => (
                                                <option key={c.value} value={c.value}>
                                                    {c.label}
                                                </option>
                                            ))}
                                        </select>
                                        <div style={modalStyles.hint}>
                                            {t("addOrganization.currencyHint")}
                                        </div>
                                    </div>

                                    <div className="form-group" style={{marginBottom: 0}}>
                                        <label style={modalStyles.label}>
                                            {t("addOrganization.stripeAccount")}
                                        </label>
                                        <select
                                            className="form-control"
                                            name="stripeAccountId"
                                            style={modalStyles.select}
                                        >
                                            <option value="">
                                                {t("addOrganization.noAccount")}
                                            </option>
                                            {stripeAccounts?.map((sa) => (
                                                <option key={sa.id} value={sa.id}>
                                                    {sa.name}
                                                </option>
                                            ))}
                                        </select>
                                        <div style={modalStyles.hint}>
                                            {t("addOrganization.stripeAccountHint")}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer" style={modalStyles.footer}>
                                <button
                                    type="button"
                                    className="btn"
                                    onClick={onClose}
                                    style={{...modalStyles.btn, ...modalStyles.btnGhost}}
                                >
                                    <i className="fas fa-times"/>
                                    {t("addOrganization.close")}
                                </button>

                                <button
                                    type="submit"
                                    className="btn"
                                    style={{...modalStyles.btn, ...modalStyles.btnPrimary}}
                                    disabled={isSubmittingDisabled}
                                    title={
                                        isSubmittingDisabled
                                            ? t("addOrganization.selectActiveProfileFirst")
                                            : t("addOrganization.createOrganization")
                                    }
                                >
                                    <i className="fas fa-check"/>
                                    {t("addOrganization.save")}
                                </button>
                            </div>
                        </Form>
                    </div>
                </div>
            </div>

            <style>{`
                #add-org-modal.modal {
                  background: rgba(0, 0, 0, 0.45);
                  backdrop-filter: blur(2px);
                }

                #add-org-modal .modal-dialog {
                  max-height: 92vh;
                  display: flex;
                  flex-direction: column;
                  margin-top: 1.25rem;
                  margin-bottom: 1.25rem;
                }

                #add-org-modal .modal-content {
                  flex: 1;
                  overflow: hidden;
                  display: flex;
                  flex-direction: column;
                }

                #add-org-modal .modal-body {
                  overflow-y: auto;
                  max-height: 66vh;
                }

                #add-org-modal .form-control:focus {
                  border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.9);
                  box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
                }

                #add-org-modal .dual-list-selector,
                #add-org-modal .dual-list {
                  margin-top: 8px;
                }

                #add-org-modal .kbola-waiver-picker-wrap {
                  border-radius: 12px;
                  border: 1px solid rgba(0, 0, 0, 0.11);
                  background: linear-gradient(180deg, rgba(255,255,255,0.94), rgba(247,248,250,0.92));
                  padding: 8px;
                }

                #add-org-modal .kbola-waiver-picker {
                  min-height: 132px;
                  height: auto !important;
                  border: none !important;
                  background: transparent !important;
                  box-shadow: none !important;
                  padding: 0 !important;
                }

                #add-org-modal .kbola-waiver-picker option {
                  border-radius: 9px;
                  margin: 2px 0;
                  padding: 9px 10px;
                  font-weight: 700;
                }

                #add-org-modal .kbola-waiver-picker option:checked {
                  background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.72);
                  color: #14151a;
                }

                #add-org-modal .kbola-waiver-picker-wrap:focus-within {
                  border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.95);
                  box-shadow: 0 0 0 0.22rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.22);
                }

                #add-org-modal .kbola-waiver-chip-row {
                  display: flex;
                  flex-wrap: wrap;
                  gap: 6px;
                  margin-top: 8px;
                }

                #add-org-modal .kbola-waiver-chip {
                  font-size: 11px;
                  font-weight: 800;
                  border-radius: 999px;
                  padding: 5px 9px;
                  color: rgba(20, 21, 26, 0.9);
                  border: 1px solid rgba(0, 0, 0, 0.1);
                  background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.42);
                  max-width: 100%;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                }

                #add-org-modal .kbola-waiver-picker::-webkit-scrollbar {
                  width: 10px;
                }

                #add-org-modal .kbola-waiver-picker::-webkit-scrollbar-thumb {
                  border-radius: 999px;
                  border: 2px solid rgba(255,255,255,0.95);
                  background: rgba(20, 21, 26, 0.24);
                }

                @media (max-width: 768px) {
                  #add-org-modal .modal-dialog {
                    max-width: 95%;
                    margin: 0.9rem auto;
                  }
                  #add-org-modal .modal-body {
                    max-height: 70vh;
                  }
                }
            `}</style>
        </>
    );
}
